/**
 * Renders page-builder blocks on the storefront. Presentation only — no
 * editing logic lives here, so this stays cheap for public SSR.
 */
import { Link } from "@tanstack/react-router";
import type { Block } from "@/lib/cms/blocks";

function paragraphs(body: unknown) {
  return String(body ?? "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);
}

function s(v: unknown) {
  return typeof v === "string" ? v : "";
}

function rows(v: unknown): Record<string, unknown>[] {
  return Array.isArray(v) ? (v as Record<string, unknown>[]) : [];
}

function Cta({ href, label }: { href: string; label: string }) {
  if (!href || !label) return null;
  const cls =
    "inline-flex items-center justify-center rounded-full bg-brand px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-brand/90";
  if (href.startsWith("/")) {
    return (
      <Link to={href} className={cls}>
        {label}
      </Link>
    );
  }
  return (
    <a href={href} className={cls}>
      {label}
    </a>
  );
}

export function BlockView({ block }: { block: Block }) {
  const d = block.data;
  const align = s(d["align"]) === "center" ? "text-center items-center" : "text-left items-start";

  switch (block.type) {
    case "hero":
      return (
        <section className="mx-auto max-w-5xl px-4 py-14 sm:py-20">
          <div className={`flex flex-col gap-5 ${align}`}>
            {s(d["eyebrow"]) ? (
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand">
                {s(d["eyebrow"])}
              </span>
            ) : null}
            {s(d["title"]) ? (
              <h1 className="font-display text-3xl font-bold leading-tight text-foreground sm:text-5xl">
                {s(d["title"])}
              </h1>
            ) : null}
            {s(d["body"]) ? (
              <p className="max-w-2xl text-base text-muted-foreground sm:text-lg">{s(d["body"])}</p>
            ) : null}
            <Cta href={s(d["ctaHref"])} label={s(d["ctaLabel"])} />
          </div>
          {s(d["image"]) ? (
            <img
              src={s(d["image"])}
              alt={s(d["title"])}
              loading="lazy"
              className="mt-10 w-full rounded-2xl border border-border object-cover"
            />
          ) : null}
        </section>
      );

    case "richText":
      return (
        <section className="mx-auto max-w-3xl px-4 py-10">
          <div className={`flex flex-col gap-4 ${align}`}>
            {s(d["title"]) ? (
              <h2 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">
                {s(d["title"])}
              </h2>
            ) : null}
            {paragraphs(d["body"]).map((p, i) => (
              <p key={i} className="text-base leading-relaxed text-muted-foreground">
                {p}
              </p>
            ))}
          </div>
        </section>
      );

    case "image":
      return s(d["image"]) ? (
        <figure
          className={
            s(d["width"]) === "full" ? "py-6" : "mx-auto max-w-5xl px-4 py-6"
          }
        >
          <img
            src={s(d["image"])}
            alt={s(d["alt"])}
            loading="lazy"
            className={`w-full object-cover ${s(d["width"]) === "full" ? "" : "rounded-2xl border border-border"}`}
          />
          {s(d["caption"]) ? (
            <figcaption className="mt-2 text-center text-xs text-muted-foreground">
              {s(d["caption"])}
            </figcaption>
          ) : null}
        </figure>
      ) : null;

    case "features":
      return (
        <section className="mx-auto max-w-5xl px-4 py-12">
          {s(d["title"]) ? (
            <h2 className="mb-8 font-display text-2xl font-semibold text-foreground sm:text-3xl">
              {s(d["title"])}
            </h2>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rows(d["items"]).map((it, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card p-5">
                <h3 className="text-sm font-semibold text-foreground">{s(it["title"])}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s(it["body"])}</p>
              </div>
            ))}
          </div>
        </section>
      );

    case "faq":
      return (
        <section className="mx-auto max-w-3xl px-4 py-12">
          {s(d["title"]) ? (
            <h2 className="mb-6 font-display text-2xl font-semibold text-foreground sm:text-3xl">
              {s(d["title"])}
            </h2>
          ) : null}
          <div className="divide-y divide-border rounded-2xl border border-border bg-card">
            {rows(d["items"]).map((it, i) => (
              <details key={i} className="group px-5 py-4">
                <summary className="cursor-pointer list-none text-sm font-semibold text-foreground">
                  {s(it["q"])}
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s(it["a"])}</p>
              </details>
            ))}
          </div>
        </section>
      );

    case "cta":
      return (
        <section className="mx-auto max-w-5xl px-4 py-12">
          <div className="flex flex-col items-center gap-4 rounded-3xl border border-border bg-card px-6 py-12 text-center">
            {s(d["title"]) ? (
              <h2 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">
                {s(d["title"])}
              </h2>
            ) : null}
            {s(d["body"]) ? (
              <p className="max-w-xl text-sm text-muted-foreground">{s(d["body"])}</p>
            ) : null}
            <Cta href={s(d["ctaHref"])} label={s(d["ctaLabel"])} />
          </div>
        </section>
      );

    case "products":
      return (
        <section className="mx-auto max-w-5xl px-4 py-12">
          {s(d["title"]) ? (
            <h2 className="mb-6 font-display text-2xl font-semibold text-foreground sm:text-3xl">
              {s(d["title"])}
            </h2>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rows(d["items"])
              .filter((it) => s(it["handle"]))
              .map((it, i) => (
                <Link
                  key={i}
                  to="/products/$handle"
                  params={{ handle: s(it["handle"]) }}
                  className="group rounded-2xl border border-border bg-card p-4 transition-shadow hover:shadow-md"
                >
                  {s(it["image"]) ? (
                    <img
                      src={s(it["image"])}
                      alt={s(it["title"]) || s(it["handle"])}
                      loading="lazy"
                      className="mb-3 aspect-[3/2] w-full rounded-xl object-cover"
                    />
                  ) : null}
                  <h3 className="text-sm font-semibold text-foreground">
                    {s(it["title"]) || s(it["handle"])}
                  </h3>
                  {s(it["body"]) ? (
                    <p className="mt-1 text-sm text-muted-foreground">{s(it["body"])}</p>
                  ) : null}
                  <span className="mt-3 inline-block text-xs font-semibold text-brand">
                    View product →
                  </span>
                </Link>
              ))}
          </div>
        </section>
      );

    case "stats":
      return (
        <section className="mx-auto max-w-5xl px-4 py-10">
          <div className="grid gap-4 rounded-2xl border border-border bg-card p-6 sm:grid-cols-3">
            {rows(d["items"]).map((it, i) => (
              <div key={i} className="text-center">
                <div className="font-display text-2xl font-bold text-foreground">
                  {s(it["value"])}
                </div>
                <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
                  {s(it["label"])}
                </div>
              </div>
            ))}
          </div>
        </section>
      );

    case "quote":
      return (
        <section className="mx-auto max-w-3xl px-4 py-12 text-center">
          <blockquote className="font-display text-xl leading-relaxed text-foreground sm:text-2xl">
            “{s(d["body"])}”
          </blockquote>
          {s(d["author"]) ? (
            <p className="mt-3 text-xs uppercase tracking-wide text-muted-foreground">
              {s(d["author"])}
            </p>
          ) : null}
        </section>
      );

    case "spacer": {
      const size = s(d["size"]);
      const h = size === "lg" ? "h-24" : size === "sm" ? "h-6" : "h-14";
      return <div className={h} aria-hidden="true" />;
    }

    default:
      return null;
  }
}

export function BlockList({ blocks }: { blocks: Block[] }) {
  return (
    <>
      {blocks.map((b) => (
        <BlockView key={b.id} block={b} />
      ))}
    </>
  );
}

export default BlockList;
